import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase.js'

// Reads vw_menu_engineering — one row per menu item with a recipe,
// classified into star / plowhorse / puzzle / dog using restaurant-
// relative thresholds (mean of units sold + mean of contribution
// margin per unit, computed inside the view).
//
// Returns:
//   rows         — sorted, all classified items
//   byClass      — { star: [], plowhorse: [], puzzle: [], dog: [] }
//   thresholds   — { avg_units, avg_cm } from the first row (any row
//                  has the same values, since they're per-restaurant)
//   hasData      — true if at least one classified item exists
//   loading, error, refresh
export function useMenuEngineering() {
  const [rows, setRows]       = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)
  const [version, setVersion] = useState(0)

  useEffect(() => {
    let mounted = true
    setLoading(true)
    setError(null)
    supabase
      .from('vw_menu_engineering')
      .select('*')
      .order('cm_total_90d', { ascending: false })
      .then(({ data, error: qErr }) => {
        if (!mounted) return
        if (qErr) {
          // eslint-disable-next-line no-console
          console.error('[useMenuEngineering] query failed', qErr)
          setError(qErr.message || 'Could not load menu engineering data.')
          setRows([])
        } else {
          setRows(data ?? [])
        }
        setLoading(false)
      })
    return () => { mounted = false }
  }, [version])

  const byClass = {
    star:      rows.filter((r) => r.classification === 'star'),
    plowhorse: rows.filter((r) => r.classification === 'plowhorse'),
    puzzle:    rows.filter((r) => r.classification === 'puzzle'),
    dog:       rows.filter((r) => r.classification === 'dog'),
  }

  const thresholds = rows[0]
    ? { avg_units: Number(rows[0].avg_units || 0), avg_cm: Number(rows[0].avg_cm || 0) }
    : null

  return {
    rows,
    byClass,
    thresholds,
    hasData: rows.length > 0,
    loading,
    error,
    refresh: () => setVersion((v) => v + 1),
  }
}
